import { useCallback, useContext, useState } from 'react';
import _ from 'underscore';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { deleteCampaign, createOrUpdateCampaign } from '@bingads-webui-campaign-react/perf-max-data';
import {
  globalizationModelSetup,
  LANGUAGE_CODE_MAPPING,
  COUNTRY_DEFAULT_LANGUAGE,
} from '@bingads-webui-campaign/ads-globalization';
import {
  loadBusinessInfoRecommendation,
  useUserCreationContext,
  useGetSupportedCountryList,
  useLoadAndUpdateUserBusinessInfo,
  useLoadAndUpdateCustomerBusinessInfo,
  useLoadAndUpdateAccountBusinessInfo,
  useGetDefaultBudgetValueForCountry,
} from '@bingads-webui-campaign-react/pmax-lite-data';
import { getUrlWithProtocol } from '@bingads-webui-campaign-react/url-utils';
import { useFloatingBannerApi } from '@bingads-webui-campaign-react/floating-banner-utils';
import {
  parsePhone,
  composePhone,
} from '@bingads-webui-campaign-react/pmax-language-country';

import { URLError } from './consts';

function isKeyIncludedInArray(arr = [], key) {
  if (_.isEmpty(arr) || !key) {
    return false;
  }
  return arr.includes(key.toLowerCase()) || arr.includes(key.toUpperCase());
}

export const useGetDataFunc = ({
  activity,
  countryPreference,
  businessInfoPrefillEnabled,
}) => {
  const serverContext = useContext(ServerContext);
  const permissions = useContext(PermissionsContext);
  const { scope } = useContext(ScopeContext);
  const {
    CurrentAccount, CurrentLocale, CurrentUser, UserRegion,
  } = serverContext || {};
  const { Market, Language } = CurrentUser || {};
  const [bizInfoData, setBizInfoData] = useState({});
  const [floatingBannerId, setFloatingBannerId] = useState(undefined);

  const { isCreatingUser, createUser, createUserPromise } = useUserCreationContext();
  const { showWarningMessage, dismissFloatingBanner } = useFloatingBannerApi();
  const { LANGUAGES } = globalizationModelSetup({ permissions, culture: CurrentLocale });
  const { getSupportedCountryList } = useGetSupportedCountryList();
  const { loadUserContactInfo, updateUserContactInfo } = useLoadAndUpdateUserBusinessInfo();
  const { loadCustomerContactInfo, updateCustomerContactInfo } = useLoadAndUpdateCustomerBusinessInfo();
  const { updateAccountContactInfo } = useLoadAndUpdateAccountBusinessInfo();
  const { getDefaultBudgetValueForCountry } = useGetDefaultBudgetValueForCountry();

  const updateBizInfoData = useCallback((data) => {
    setBizInfoData((prevState) => {
      const newState = {
        ...prevState,
        ...data,
      };
      return _.isEqual(prevState, newState) ? prevState : newState;
    });
  }, []);

  const getPreferredLanguage = useCallback((preferCountryCode = 'US') => {
    let preferLanguage = 'English';
    const countryDefaultLanguage = COUNTRY_DEFAULT_LANGUAGE[preferCountryCode];
    if (LANGUAGES.includes(countryDefaultLanguage)) {
      preferLanguage = countryDefaultLanguage;
    } else {
      const browserLanguageCode = _.get(navigator, ['language'], 'en').substring(0, 2);
      const userLanguageCode = (Language || browserLanguageCode).toLowerCase();
      if (userLanguageCode && LANGUAGE_CODE_MAPPING[userLanguageCode]
        && LANGUAGES.includes(LANGUAGE_CODE_MAPPING[userLanguageCode])) {
        preferLanguage = LANGUAGE_CODE_MAPPING[userLanguageCode];
      }
    }

    return preferLanguage;
  }, [LANGUAGES, Language]);

  const createUserFunc = useCallback(() => {
    if (CurrentAccount) {
      return Promise.resolve(CurrentAccount);
    }

    if (!isCreatingUser) {
      // Trigger creation; errors are propagated through createUserPromise via rejectLastCreatedPromise
      createUser().catch(_.noop);
    }

    // Always return createUserPromise (resolves with newServerContext which has proper .Id fields),
    // not the return value of createUser() which resolves with the raw API response.
    return createUserPromise
      .catch((error) => {
        activity.error(`create user failed - error: ${JSON.stringify(error)}.`);
        const bannerId = showWarningMessage(_TL_('Oops! An error has occurred. Please try again later.'));
        setFloatingBannerId(bannerId);
        return Promise.reject(error);
      });
  }, [CurrentAccount, activity, createUser, createUserPromise, isCreatingUser, showWarningMessage]);

  const deleteCampaignPromise = useCallback(
    () => {
      const { campaignId } = scope.values;
      return deleteCampaign(campaignId)
        .then(() => {
          scope.remove(['campaignId', 'assetGroupId']);
          scope.save();
        })
        .catch((error) => {
          activity.error(`delete campaign failed - error: ${JSON.stringify(error)}.`);
        });
    }
    , [activity, scope]
  );

  const updateCampaignPromise = useCallback(({ currencyPreference, country, language }) => {
    const { campaignId } = scope.values;
    let campaignPromise = () => Promise.resolve();
    if (campaignId) {
      // when currencyPreference is set, it means user already set budget value,
      // we should not update the budget value.
      campaignPromise = () => getDefaultBudgetValueForCountry(country)
        .then(budgetValue => createOrUpdateCampaign({
          Id: campaignId,
          Languages: [language],
          ...(currencyPreference ? {} : {
            Budget: {
              Amount: budgetValue,
              Type: 'DailyStandard',
            },
          }),
        }).catch((error) => {
          activity.error(`Failed to update campaign. Error: ${JSON.stringify(error)}`);
          throw error;
        }));
    }

    return campaignPromise();
  }, [activity, getDefaultBudgetValueForCountry, scope]);

  const getSupportedCountryListPromise = useCallback(() => {
    if (_.isEmpty(bizInfoData.supportedCountryList)) {
      return getSupportedCountryList()
        .then((supportedCountryList) => {
          let preferCountryCode = countryPreference;
          if (!businessInfoPrefillEnabled) {
            const userCountry = UserRegion || Market;
            const supportedCountryCodeList = _.map(supportedCountryList, c => c.Code);
            if (isKeyIncludedInArray(supportedCountryCodeList, userCountry)) {
              preferCountryCode = userCountry.toUpperCase();
            } else {
              preferCountryCode = 'US';
            }
          }

          updateBizInfoData({
            countryCode: preferCountryCode,
            callingCountryCode: preferCountryCode,
            callingCode: _.get(_.find(supportedCountryList, { Code: preferCountryCode }), ['CountryCallingCode'], '1'),
            supportedCountryList,
          });

          return supportedCountryList;
        })
        .catch((error) => {
          activity.trace(`getSupportedCountryList failed - error: ${JSON.stringify(error)}.`);
        });
    }

    return Promise.resolve(bizInfoData.supportedCountryList);
  }, [Market, UserRegion, activity, bizInfoData.supportedCountryList, businessInfoPrefillEnabled,
    countryPreference, getSupportedCountryList, updateBizInfoData]);

  const loadCustomerContactInfoPromise = useCallback(() => {
    if (!bizInfoData.customerId) {
      return loadCustomerContactInfo()
        .then((customerContactInfo) => {
          if (_.isEmpty(customerContactInfo)) {
            activity.trace('loadCustomerContactInfo - customer contact info is empty');
            return {};
          }

          const {
            phone: customerPhone,
            businessName: customerBusinessName,
            customerId,
          } = customerContactInfo || {};

          const customerInfo = {
            customerId,
            customerBusinessName,
            customerPhone,
          };

          activity.trace(`loadCustomerContactInfo - customer info: ${customerId}`);

          updateBizInfoData({
            ...customerInfo,
          });

          return customerInfo;
        })
        .catch((error) => {
          activity.trace(`loadCustomerContactInfo failed - error: ${JSON.stringify(error)}.`);
        });
    }

    return Promise.resolve(bizInfoData);
  }, [activity, bizInfoData, loadCustomerContactInfo, updateBizInfoData]);

  const loadUserContactInfoPromise = useCallback(() => {
    if (!bizInfoData.userEmail) {
      return Promise.all([
        loadUserContactInfo(),
        getSupportedCountryListPromise(),
      ])
        .then(([userContactInfo, supportedCountryList]) => {
          if (_.isEmpty(userContactInfo)) {
            activity.trace('loadUserContactInfo - user contact info is empty');
            return {};
          }

          const {
            phone: userPhone,
            email: userEmail,
            phoneNumberCountryCode,
            allowContact,
            allowContactByPhone,
          } = userContactInfo || {};

          let userInfo = {
            email: userEmail,
            userEmail,
            allowContact,
            allowContactByPhone,
          };

          if (userPhone) {
            const {
              phoneNumber: userPhoneNumber,
              callingCode: userCallingCode,
              callingCountryCode: userCallingCountryCode,
            } = parsePhone(userPhone, supportedCountryList, phoneNumberCountryCode);

            userInfo = {
              ...userInfo,
              userPhoneNumber,
              userCallingCode,
              userCallingCountryCode,
            };
          }
          activity.trace('loadUserContactInfo - user info: [redacted]');
          updateBizInfoData({
            ...userInfo,
          });

          return userInfo;
        })
        .catch((error) => {
          activity.trace(`loadUserContactInfo failed - error: ${JSON.stringify(error)}.`);
        });
    }

    return Promise.resolve(bizInfoData);
  }, [activity, bizInfoData, getSupportedCountryListPromise, loadUserContactInfo, updateBizInfoData]);

  const loadBISuggestionsAndData = useCallback((url) => {
    if (!url) {
      activity.trace('loadBISuggestions - url is empty');
      return Promise.reject();
    }

    if (floatingBannerId) {
      dismissFloatingBanner(floatingBannerId);
      setFloatingBannerId(undefined);
    }

    const payload = {
      url: getUrlWithProtocol(url),
      language: bizInfoData.language || 'English',
    };

    activity.trace(`loadBISuggestions - payload: ${JSON.stringify(payload)}`);

    updateBizInfoData({
      isLoadingBI: true,
      loadComplete: false,
    });

    // createUser must complete first - cid/aid are required for GetExtractedBusinessInfo auth.
    // After creation resolves, loadBI and getSupportedCountryList run in parallel.
    return createUserFunc()
      .then((account) => {
        if (!_.isEmpty(account)
          && _.has(account, 'CurrentAccount')
          && _.has(account, 'CurrentCustomer')) {
          const aid = _.get(account, ['CurrentAccount', 'Id']);
          const cid = _.get(account, ['CurrentCustomer', 'Id']);
          return Promise.all([
            loadBusinessInfoRecommendation(payload, cid, aid).timeout(10000),
            getSupportedCountryListPromise(),
          ]);
        }

        const aid = _.get(account, ['Id']);
        return Promise.all([
          loadCustomerContactInfoPromise()
            .then((customer) => {
              const cid = _.get(customer, ['customerId']);
              return loadBusinessInfoRecommendation(payload, cid, aid).timeout(10000);
            }),
          getSupportedCountryListPromise(),
        ]);
      })
      .then(([businessInfoResponse]) => {
        const {
          BusinessName,
          InternationalPhoneNumber,
          PhoneNumber,
          Addresses,
        } = businessInfoResponse || {};

        const bizCountryCode = _.get(_.first(Addresses), 'CountryCode') || bizInfoData.countryCode;
        const {
          phoneNumber: bizPhoneNumber,
          callingCode: bizCallingCode,
          callingCountryCode,
        } = parsePhone(InternationalPhoneNumber || PhoneNumber, bizInfoData.supportedCountryList, bizCountryCode);

        const newBizInfoData = {
          ...(BusinessName ? { businessName: BusinessName } : {}),
          ...(bizPhoneNumber ? { phoneNumber: bizPhoneNumber } : {}),
          ...(bizCallingCode ? { callingCode: bizCallingCode } : {}),
          ...(callingCountryCode ? { callingCountryCode } : {}),
        };

        updateBizInfoData(newBizInfoData);

        return Promise.resolve(newBizInfoData);
      })
      .catch((error) => {
        let errMsg = _TL_('Oops! An error has occurred. Please try again later.');
        const errorCode = _.get(error, ['responseJSON', 'value', 0, 'Code']);
        if (errorCode && URLError[errorCode]
          && _.isString(URLError[errorCode])) {
          errMsg = URLError[errorCode];
        }
        const bannerId = showWarningMessage(errMsg);
        setFloatingBannerId(bannerId);
        activity.trace(`loadBISuggestions failed - error: ${JSON.stringify(error)}.`);
      })
      .finally(() => {
        updateBizInfoData({
          loadComplete: true,
          isLoadingBI: false,
        });
      });
  }, [activity, bizInfoData.countryCode, bizInfoData.language, bizInfoData.supportedCountryList,
    createUserFunc, dismissFloatingBanner, floatingBannerId, getSupportedCountryListPromise,
    loadCustomerContactInfoPromise, showWarningMessage, updateBizInfoData]);

  const saveAccountCustomerUserData = useCallback(({
    phoneNumber, callingCountryCode, callingCode, businessName, email, terms = {},
  }) => {
    const {
      phoneNumber: userPhoneNumber,
      countryCode: usercallingCountryCode,
    } = composePhone(phoneNumber, callingCountryCode, callingCode);

    const updateAccountPromise = () => updateAccountContactInfo({
      businessName,
    });
    const updateCustomerPromise = () => updateCustomerContactInfo({
      businessName,
      phone: userPhoneNumber,
    });
    const updateUserPromise = () => updateUserContactInfo({
      phoneNumberCountryCode: usercallingCountryCode,
      phone: userPhoneNumber,
      email,
    }, terms.communication, true);

    return Promise.all([
      updateAccountPromise(),
      updateCustomerPromise(),
      updateUserPromise(),
    ])
      .then(() => {
        activity.trace('update business info successfully');
      })
      .catch((error) => {
        activity.error(`update business info failed - error: ${JSON.stringify(error)}.`);
        const bannerId = showWarningMessage(_TL_('Oops! An error has occurred. Please try again later.'));
        setFloatingBannerId(bannerId);
        return Promise.reject(error);
      });
  }, [activity, showWarningMessage, updateAccountContactInfo, updateCustomerContactInfo, updateUserContactInfo]);

  return {
    bizInfoData,
    updateBizInfoData,

    getPreferredLanguage,
    createUserFunc,
    deleteCampaignPromise,
    updateCampaignPromise,
    getSupportedCountryListPromise,
    loadCustomerContactInfoPromise,
    loadUserContactInfoPromise,
    loadBISuggestionsAndData,
    saveAccountCustomerUserData,
  };
};
